import axios from "../configs/axios";
import {
  ApiResponse,
  Course,
  CourseLesson,
  CourseProgressDto,
  CourseSection,
  LessonSettings,
  QuizSubmitRequest,
  QuizSubmitResponse,
} from "../types/course.types";

export type { ApiResponse } from "../types/course.types";

const DEFAULT_SETTINGS: LessonSettings = {
  requireCompletion: false,
};

/** Backfill new required lesson fields on legacy/partial lesson data. */
const normalizeLesson = (lesson: any): CourseLesson => {
  const type: CourseLesson["type"] = lesson?.type ?? "video";
  const settings: LessonSettings = {
    ...DEFAULT_SETTINGS,
    ...(lesson?.settings ?? {}),
  };
  const normalized: CourseLesson = {
    ...lesson,
    id: lesson?.id,
    title: lesson?.title ?? "",
    type,
    settings,
  };
  if (type === "quiz") {
    normalized.passPercent = lesson?.passPercent ?? 70;
  }
  return normalized;
};

/**
 * Client-side back-compat safety net. The backend already returns normalized
 * `sections`, but if a course ever comes back with flat `lessons`, wrap them
 * into a single default section so the player always works against `sections`.
 */
const normalizeCourse = (raw: any): Course => {
  let sections: CourseSection[];
  if (Array.isArray(raw?.sections) && raw.sections.length > 0) {
    sections = raw.sections.map((s: any) => ({
      id: s?.id ?? "section",
      title: s?.title ?? "",
      lessons: Array.isArray(s?.lessons) ? s.lessons.map(normalizeLesson) : [],
    }));
  } else if (Array.isArray(raw?.lessons) && raw.lessons.length > 0) {
    sections = [
      {
        id: "legacy-default",
        title: "",
        lessons: raw.lessons.map(normalizeLesson),
      },
    ];
  } else {
    sections = [];
  }
  const course: Course = { ...raw, sections };
  delete (course as any).lessons;
  return course;
};

// Courses require a logged-in partner, so we use the authenticated axios.
export const courseApi = {
  getAllCourses: async (): Promise<ApiResponse<Course[]>> => {
    const response = await axios.get("/courses");
    const body: ApiResponse<any[]> = response.data;
    return { ...body, data: (body.data || []).map(normalizeCourse) };
  },

  getCourseById: async (id: string): Promise<ApiResponse<Course>> => {
    const response = await axios.get(`/courses/${id}`);
    const body: ApiResponse<any> = response.data;
    return { ...body, data: normalizeCourse(body.data) };
  },

  getProgress: async (
    courseId: string
  ): Promise<ApiResponse<CourseProgressDto>> => {
    const response = await axios.get(`/courses/${courseId}/progress`);
    return response.data;
  },

  setLessonCompletion: async (
    courseId: string,
    lessonId: string,
    completed: boolean
  ): Promise<ApiResponse<CourseProgressDto>> => {
    const response = await axios.post(`/courses/${courseId}/progress`, {
      lessonId,
      completed,
    });
    return response.data;
  },

  submitQuiz: async (
    courseId: string,
    payload: QuizSubmitRequest
  ): Promise<ApiResponse<QuizSubmitResponse>> => {
    const response = await axios.post(
      `/courses/${courseId}/quiz-submit`,
      payload
    );
    return response.data;
  },

  getResourceDownloadUrl: async (
    fileKey: string
  ): Promise<ApiResponse<{ downloadUrl: string }>> => {
    const response = await axios.get(`/courses/resource-download`, {
      params: { key: fileKey },
    });
    return response.data;
  },
};

/** Total lessons across all sections (denominator for percent). */
export const countPublishedLessons = (course: Course): number =>
  (course.sections || []).reduce(
    (sum, s) => sum + (s.lessons || []).length,
    0
  );

/** Flatten sections into an ordered list of lessons. */
export const flattenPublishedLessons = (course: Course): CourseLesson[] =>
  (course.sections || []).flatMap((s) => s.lessons || []);

/** YouTube/Vimeo embeddable src; Zoom/other returned as-is. */
export const toEmbedUrl = (url: string, source?: string): string => {
  if (!url) return url;
  if (source === "youtube" || /youtu\.?be/.test(url)) {
    const m = url.match(
      /(?:youtu\.be\/|v=|\/embed\/|\/shorts\/)([A-Za-z0-9_-]{6,})/
    );
    return m?.[1] ? `https://www.youtube.com/embed/${m[1]}` : url;
  }
  if (source === "vimeo" || /vimeo\.com/.test(url)) {
    const m = url.match(/vimeo\.com\/(?:video\/)?(\d+)/);
    return m?.[1] ? `https://player.vimeo.com/video/${m[1]}` : url;
  }
  return url;
};

export const getThumbnailUrl = (
  url?: string,
  source?: string
): string | null => {
  if (!url) return null;
  if (source === "youtube" || /youtu\.?be/.test(url)) {
    const m = url.match(
      /(?:youtu\.be\/|v=|\/embed\/|\/shorts\/)([A-Za-z0-9_-]{6,})/
    );
    return m?.[1] ? `https://img.youtube.com/vi/${m[1]}/hqdefault.jpg` : null;
  }
  return null;
};

export const isEmbedReliable = (source?: string): boolean =>
  source === "youtube" || source === "vimeo";

/** Extract the YouTube video id from a URL (for the IFrame Player API). */
export const getYoutubeId = (url?: string): string | null => {
  if (!url) return null;
  const m = url.match(
    /(?:youtu\.be\/|v=|\/embed\/|\/shorts\/)([A-Za-z0-9_-]{6,})/
  );
  return m?.[1] ?? null;
};

export const isYouTube = (url?: string, source?: string): boolean =>
  source === "youtube" || (!!url && /youtu\.?be/.test(url));
